/**
 * THE REALM-SCOPED TRACE ID and the outbound `traceparent` composed on it -- the ORIGINATING edge's half of W3C Trace
 * Context, whose grammar, parser and minters stay in `const/w3c-trace-context.ts`.
 *
 * WHY THIS HOME (2026-09-08, row 79 of the terminal arm: why-four-const-memos-stand-as-silent-exceptions-because-the-
 * convention-never-named-the-purpose-they-share). The page-load id is module state, deliberately: the realm boundary
 * IS the correlation boundary this needs -- in a browser a module instance lives exactly as long as the page load, so
 * "one trace per page load" is the module's own lifetime, and a full navigation gets a fresh id for free. That is state
 * with a ground of its own, not a scan memo (`memoizeByOwner` would demand a clear that means nothing here), and the
 * const floor holds data and pure transforms only (`QUAL-CONST-MODULE-SCOPE-MUTABLE-BINDING-001`), so the binding
 * lives on this floor beside its one consumer's (`components/lib/api/traced-edge-fetch.ts`). The WIDTHS and the
 * randomness source stay in the const leaf, spelled once beside the parser that enforces them, and this leaf composes
 * `mintTraceIdHex` / `mintSpanIdHex` / `formatTraceparent` and names none of its own.
 *
 * NO WORKER CALLS THIS, and none may: a Worker isolate is shared across unrelated requests, so a realm-scoped id there
 * would fuse every visitor's requests onto one trace. Server-side, an inbound trace arrives on the header and is read
 * through `inboundTraceEnv`; there is nothing here for that path to want.
 *
 * Re-minted on each call while randomness is unavailable (the `''` arm), so a source that appears later is picked up
 * rather than latched off.
 */

import { formatTraceparent, mintSpanIdHex, mintTraceIdHex } from '@federa/const/w3c-trace-context';

/** The trace id for THIS JS realm, minted once and reused. */
let pageLoadTraceId = '';

/** The realm-scoped read of the same-file `pageLoadTraceId`, minted through the const leaf's trace-id minter. */
function currentPageLoadTraceId(): string {
	if (pageLoadTraceId === '') pageLoadTraceId = mintTraceIdHex();
	return pageLoadTraceId;
}

/** A `traceparent` for ONE outbound request: this realm's trace id and a FRESH span id, so sibling requests are distinct spans of one trace rather than one span reported N times. `''` when no trace can be minted, which the caller reads as "send no header".
 *  @runtime-derived-ssot-helper: composes the same-file `currentPageLoadTraceId` with the const leaf's `mintSpanIdHex` and `formatTraceparent`, so a caller names no width, no version and no flag byte of its own. */
export function newRequestTraceparent(): string {
	return formatTraceparent({ traceId: currentPageLoadTraceId(), spanId: mintSpanIdHex() });
}
